import { Injectable } from "@angular/core";
import { CulturalActivityStatus } from "@common/ADAPT.Common.Model/organisation/cultural-activity-status";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Vision } from "@common/ADAPT.Common.Model/organisation/vision";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { UserService } from "@common/user/user.service";
import { ActivitiesService } from "@org-common/lib/activities/activities.service";
import { ActivityHandler } from "@org-common/lib/activities/activity-handler";
import { NonCulturalActivityType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import { VisionService } from "@org-common/lib/vision/vision.service";
import { lastValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";

@Injectable()
export class VisionActivityHandler extends ActivityHandler<Team> {
    public readonly entityType = Team;
    public readonly activityTypes = [NonCulturalActivityType.Visions];

    constructor(
        private visionService: VisionService,
        private rxjsBreezeService: RxjsBreezeService,
        private userService: UserService,
    ) {
        super();
    }

    public get shouldUpdate$() {
        return this.rxjsBreezeService.entityTypeChanged(Vision).pipe(
            filter((vision) => vision.personId === this.userService.currentPerson?.personId),
            map(() => [NonCulturalActivityType.Visions]),
        );
    }

    public async getGroupedActivities(personId: number, _startDate?: Date, _endDate?: Date, excludeCompleted?: boolean) {
        const group = ActivitiesService.generateActivitiesGroupObject();

        const visionEntities = await lastValueFrom(this.visionService.getVisionsForPerson(personId));
        const visionTeams = visionEntities.reduce<Team[]>((acc, v) => {
            if (v.team && !acc.find((t) => t.teamId === v.teamId)) {
                acc.push(v.team);
            }

            return acc;
        }, []);

        const ownerCompletedTeams: Team[] = [];
        group.totalCountByStatus = ActivitiesService.generateActivitiesGroupedByStatusCountObject();
        for (const team of visionTeams) {
            const teamVisions = visionEntities.filter((v) => v.teamId === team.teamId);
            if (this.visionService.areAllVisionsCompleted(teamVisions)) {
                ownerCompletedTeams.push(team);
            } else {
                group.totalCountByStatus[CulturalActivityStatus.Overdue]++;
            }
        }

        group[CulturalActivityStatus.Overdue] = visionTeams.filter((team) => !ownerCompletedTeams.some((ct) => ct.teamId === team.teamId));
        group.allItems = excludeCompleted
            ? group[CulturalActivityStatus.Overdue]
            : visionTeams;
        group.totalCount = visionTeams.length;
        group.displayedCount = group.allItems.length;

        // Try out prompting for integrated vision if all team members have completed their personal visions
        if (ownerCompletedTeams.length > 0 && excludeCompleted) {
            const incompleteIntegratedVisionTeams: Team[] = [];
            // check teams where the person has completed their vision to see if all other members have completed too
            // - prompt for integrated vision so all team members can see it
            for (const team of ownerCompletedTeams) {
                const teamVisions = await lastValueFrom(this.visionService.getVisionsByTeam(team.teamId));
                const memberVisions = teamVisions.filter((v) => v.extensions.isPersonal);
                const integratedVisions = teamVisions.filter((v) => v.extensions.isIntegrated);
                const isPersonForIntegratedVision = integratedVisions.some((v) => v.personId === personId);
                if (isPersonForIntegratedVision && this.visionService.areAllVisionsCompleted(memberVisions) && !this.visionService.areAllVisionsCompleted(integratedVisions)) {
                    incompleteIntegratedVisionTeams.push(team);
                }
            }

            if (incompleteIntegratedVisionTeams.length > 0) {
                group.allItems.push(...incompleteIntegratedVisionTeams);
                group.displayedCount += incompleteIntegratedVisionTeams.length;
            }
        }

        return { [NonCulturalActivityType.Visions]: group };
    }

    public async getCalendarEventForActivity(activity: Team) {
        const visions = await lastValueFrom(this.visionService.getVisionsByPersonTeam(this.userService.currentPerson!.personId, activity.teamId));
        const completed = this.visionService.areAllVisionsCompleted(visions);
        return {
            title: activity.name,
            startDate: completed
                ? visions.reduce<Date | undefined>((acc, v) => {
                    if (!acc) {
                        return v.lastUpdated;
                    } else if (v.lastUpdated && v.lastUpdated > acc) {
                        return v.lastUpdated;
                    }

                    return acc;
                }, undefined)
                : new Date(),
            endDate: undefined,
            completed,
        };
    }
}
